import React, { useCallback, useRef } from "react";

import { Box } from "@/components/atoms/Box";
import { selectedPlayerIdVar } from "@/features/player-kanban/stores/kanbanReactiveVars";

/**
 * Internal state interface for gesture detection tracking
 */
interface GestureState {
  isDown: boolean;
  startTime: number;
  startPos: { x: number; y: number };
  isDragging: boolean;
  dragStarted: boolean;
}

/**
 * Props for PlayerCardGestureHandler component
 */
interface PlayerCardGestureHandlerProps {
  playerId: string;
  playerName: string;
  onPress: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  children: React.ReactNode;
}

/**
 * Container component managing custom gesture detection for player cards.
 * Distinguishes between clicks and drag operations with threshold-based detection.
 * @param root0 - Component props
 * @param root0.playerId - Unique identifier for the player
 * @param root0.playerName - Full name of the player for logging
 * @param root0.onPress - Callback invoked on click/tap (not drag)
 * @param root0.onDragStart - Callback invoked when drag threshold is exceeded
 * @param root0.onDragEnd - Callback invoked when drag operation completes
 * @param root0.children - Child elements to wrap with gesture detection
 * @remarks Uses Box component instead of HTML div for cross-platform compatibility with React Native.
 * Gesture props (onMouseDown, onMouseMove, etc.) are spread to Box component.
 */
const PlayerCardGestureHandler = ({
  playerId,
  playerName,
  onPress,
  onDragStart,
  onDragEnd,
  children,
}: PlayerCardGestureHandlerProps) => {
  const gestureState = useRef<GestureState>({
    isDown: false,
    startTime: 0,
    startPos: { x: 0, y: 0 },
    isDragging: false,
    dragStarted: false,
  });

  const DRAG_THRESHOLD = 8; // pixels
  const CLICK_TIME_THRESHOLD = 300; // milliseconds

  const resetGestureState = useCallback(() => {
    gestureState.current = {
      isDown: false,
      startTime: 0,
      startPos: { x: 0, y: 0 },
      isDragging: false,
      dragStarted: false,
    };
  }, []);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    const { clientX = 0, clientY = 0 } = event;
    gestureState.current = {
      isDown: true,
      startTime: Date.now(),
      startPos: { x: clientX, y: clientY },
      isDragging: false,
      dragStarted: false,
    };
  }, []);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches?.[0];
    if (touch) {
      gestureState.current = {
        isDown: true,
        startTime: Date.now(),
        startPos: { x: touch.clientX, y: touch.clientY },
        isDragging: false,
        dragStarted: false,
      };
    }
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (gestureState.current.isDown && !gestureState.current.isDragging) {
        const { clientX = 0, clientY = 0 } = event;
        const deltaX = clientX - gestureState.current.startPos.x;
        const deltaY = clientY - gestureState.current.startPos.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > DRAG_THRESHOLD) {
          gestureState.current = { ...gestureState.current, isDragging: true };
          if (!gestureState.current.dragStarted) {
            gestureState.current = {
              ...gestureState.current,
              dragStarted: true,
            };
            console.log(`🚀 [DRAG] ${playerName}`);
            onDragStart?.();
          }
        }
      }
    },
    [playerName, onDragStart]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      const touch = event.touches?.[0];
      if (
        touch &&
        gestureState.current.isDown &&
        !gestureState.current.isDragging
      ) {
        const deltaX = touch.clientX - gestureState.current.startPos.x;
        const deltaY = touch.clientY - gestureState.current.startPos.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > DRAG_THRESHOLD) {
          gestureState.current = {
            ...gestureState.current,
            isDragging: true,
          };
          if (!gestureState.current.dragStarted) {
            gestureState.current = {
              ...gestureState.current,
              dragStarted: true,
            };
            console.log(`🚀 [DRAG] ${playerName}`);
            onDragStart?.();
          }
        }
      }
    },
    [playerName, onDragStart]
  );

  const handleMouseUp = useCallback(() => {
    if (gestureState.current.isDown) {
      const duration = Date.now() - gestureState.current.startTime;

      if (gestureState.current.isDragging) {
        // This was a drag operation
        onDragEnd?.();
      } else if (duration < CLICK_TIME_THRESHOLD) {
        // This was a click
        console.log(`🖱️ [CLICK] ${playerName} - TRIGGERING SIDEBAR`);
        console.log(
          "🔧 [DEBUG] About to call setSelectedPlayer with:",
          playerId
        );
        try {
          selectedPlayerIdVar(playerId);
          console.log("✅ [DEBUG] setSelectedPlayer called successfully");
        } catch (error) {
          console.error("❌ [ERROR] setSelectedPlayer failed:", error);
        }
        onPress();
      }
    }
    resetGestureState();
  }, [onPress, onDragEnd, playerName, resetGestureState, playerId]);

  const handleTouchEnd = useCallback(() => {
    if (gestureState.current.isDown) {
      const duration = Date.now() - gestureState.current.startTime;

      if (gestureState.current.isDragging) {
        // This was a drag operation
        onDragEnd?.();
      } else if (duration < CLICK_TIME_THRESHOLD) {
        // This was a click
        console.log(`🖱️ [CLICK] ${playerName} - TRIGGERING SIDEBAR`);
        console.log(
          "🔧 [DEBUG] About to call setSelectedPlayer with:",
          playerId
        );
        try {
          selectedPlayerIdVar(playerId);
          console.log("✅ [DEBUG] setSelectedPlayer called successfully");
        } catch (error) {
          console.error("❌ [ERROR] setSelectedPlayer failed:", error);
        }
        onPress();
      }
    }
    resetGestureState();
  }, [onPress, onDragEnd, playerName, resetGestureState, playerId]);

  // Disable custom gesture detection if onDragStart is not provided (@dnd-kit is handling it)
  const useCustomGestures = !!onDragStart;

  const gestureProps = useCustomGestures
    ? {
        onMouseDown: handleMouseDown,
        onMouseMove: handleMouseMove,
        onMouseUp: handleMouseUp,
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
      }
    : {};

  return <Box {...gestureProps}>{children}</Box>;
};

export default PlayerCardGestureHandler;
